import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import CheckboxChecked from 'src/assets/images/checkbox-checked.svg';
import CheckboxUnchecked from 'src/assets/images/checkbox-unchecked.svg';
import ChevronDown from 'src/assets/images/chevron-down.svg';
import { validateFormValues, } from './types';
import TermsAndConditions from 'src/components/flows/RegistrationFlow/shared/TermsAndConditions';
// Terms and conditions link configuration
const TERMS_LINKS = {
    termsAndConditions: { text: 'Terms & Conditions', href: '#' },
    officialRules: {
        text: 'Intuit Hour of Finance Challenge Official Rules',
        href: '#',
    },
    termsOfService: { text: 'Prosperity Quest Terms of Service', href: '#' },
    privacyStatement: { text: 'Intuit Global Privacy Statement', href: '#' },
};
/**
 * Gets localized string value for given locale
 * Falls back to en-US if locale not found
 */
const getLocalizedValue = (localizedStr, locale) => {
    if (!localizedStr)
        return '';
    return localizedStr[locale] || localizedStr['en-US'] || '';
};
/**
 * Gets the label text with an asterisk if the field is required
 */
const getFieldLabel = (field, locale) => {
    const label = getLocalizedValue(field.title, locale);
    return field.validation?.required ? `${label} *` : label;
};
/**
 * Renders a text/email/number input field
 */
const renderTextInput = (field, value, onChange, locale, classNames, isReadOnly, isDisabled) => {
    const inputType = field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text';
    const inputClassName = `${classNames?.input || ''} ${isReadOnly || isDisabled ? classNames?.inputDisabled || '' : ''}`.trim();
    return (_jsxs("div", { className: classNames?.field, children: [_jsx("label", { htmlFor: field.fieldName, className: classNames?.fieldLabel, children: getFieldLabel(field, locale) }), _jsx("div", { className: classNames?.inputWrapper, children: _jsx("input", { id: field.fieldName, type: inputType, className: inputClassName, value: value, onChange: (e) => onChange(e.target.value), placeholder: getLocalizedValue(field.placeholder, locale), readOnly: isReadOnly, disabled: isDisabled, min: field.type === 'number' ? field.validation?.min?.toString() : undefined, max: field.type === 'number' ? field.validation?.max?.toString() : undefined }) }), field.helperText && (_jsx("p", { className: classNames?.helperText, children: getLocalizedValue(field.helperText, locale) }))] }, field.fieldName));
};
/**
 * Renders a dropdown/select field
 * Note: Does not render children - those are handled in renderField
 */
const renderDropdown = (field, value, onChange, locale, classNames, isDisabled) => (_jsxs("div", { className: classNames?.field, children: [_jsx("label", { htmlFor: field.fieldName, className: classNames?.fieldLabel, children: getFieldLabel(field, locale) }), _jsxs("div", { className: classNames?.dropdownWrapper, children: [_jsxs("select", { id: field.fieldName, className: classNames?.dropdown, value: value, onChange: (e) => onChange(e.target.value), disabled: isDisabled, children: [_jsx("option", { value: "", children: getLocalizedValue(field.placeholder, locale) || 'Select option' }), field.options?.map((option) => (_jsx("option", { value: option.value, children: getLocalizedValue(option.title, locale) }, option.value)))] }), _jsx("img", { src: ChevronDown, alt: "", className: classNames?.dropdownIcon, "aria-hidden": "true" })] })] }, field.fieldName));
/**
 * Gets the children fields for a selected dropdown option
 */
const getDropdownChildren = (field, selectedValue) => {
    if (!field.options || !selectedValue)
        return [];
    const selectedOption = field.options.find(opt => opt.value === selectedValue);
    return selectedOption?.children || [];
};
/**
 * Custom multi-select dropdown component with checkboxes
 */
const MultiSelectDropdown = ({ field, selectedValues, onChange, locale, classNames, isDisabled }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const dropdownRef = React.useRef(null);
    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
    const handleToggle = (value) => {
        const newValues = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onChange(newValues);
    };
    const getDisplayText = () => {
        if (selectedValues.length === 0) {
            return getLocalizedValue(field.placeholder, locale) || 'Select';
        }
        if (selectedValues.length === 1) {
            const option = field.options?.find(opt => opt.value === selectedValues[0]);
            return option ? getLocalizedValue(option.title, locale) : selectedValues[0];
        }
        return `${selectedValues.length} selected`;
    };
    return (_jsxs("div", { className: classNames?.field, ref: dropdownRef, children: [_jsx("label", { htmlFor: field.fieldName, className: classNames?.fieldLabel, children: getFieldLabel(field, locale) }), _jsxs("div", { className: classNames?.dropdownWrapper, style: { position: 'relative' }, children: [_jsx("button", { id: field.fieldName, type: "button", className: classNames?.dropdown, onClick: () => !isDisabled && setIsOpen(!isOpen), disabled: isDisabled, style: {
                            width: '100%',
                            textAlign: 'left',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }, children: getDisplayText() }), _jsx("img", { src: ChevronDown, alt: "", className: classNames?.dropdownIcon, "aria-hidden": "true", style: {
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease',
                        } }), isOpen && (_jsx("div", { style: {
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '300px',
                            overflowY: 'auto',
                            backgroundColor: '#ffffff',
                            border: '1px solid rgba(0, 37, 74, 0.3)',
                            borderRadius: '14px',
                            marginTop: '4px',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        }, children: field.options?.map((option) => {
                            const isChecked = selectedValues.includes(option.value);
                            return (_jsxs("button", { type: "button", onClick: () => handleToggle(option.value), style: {
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontSize: '16px',
                                    color: '#21262a',
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.backgroundColor = '#f4f4f4';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }, children: [_jsx("img", { src: isChecked ? CheckboxChecked : CheckboxUnchecked, alt: "", "aria-hidden": "true", style: { width: '24px', height: '24px', flexShrink: 0 } }), _jsx("span", { style: { flex: 1 }, children: getLocalizedValue(option.title, locale) })] }, option.value));
                        }) }))] }), field.helperText && (_jsx("p", { className: classNames?.helperText, children: getLocalizedValue(field.helperText, locale) }))] }));
};
/**
 * Renders a multi-select dropdown with checkboxes
 */
const renderMultiSelectDropdown = (field, selectedValues, onChange, locale, classNames, isDisabled) => (_jsx(MultiSelectDropdown, { field: field, selectedValues: selectedValues, onChange: onChange, locale: locale, classNames: classNames, isDisabled: isDisabled }, field.fieldName));
/**
 * Renders a checkbox group field (multi-select checkboxes)
 */
const renderCheckboxGroup = (field, selectedValues, onToggle, locale, classNames) => (_jsxs("div", { className: classNames?.radioGroup, children: [_jsx("div", { className: classNames?.radioGroupHeader, children: _jsx("div", { className: classNames?.fieldLabel, children: getFieldLabel(field, locale) }) }), _jsx("div", { className: classNames?.radioOptions, children: field.options?.map((option) => {
                const isChecked = selectedValues.includes(option.value);
                return (_jsxs("button", { type: "button", className: `${classNames?.radioOption || ''} ${isChecked ? classNames?.radioOptionChecked || 'checked' : ''}`.trim(), onClick: () => onToggle(option.value), children: [_jsx("div", { className: classNames?.radioIcon, children: _jsx("img", { src: isChecked ? CheckboxChecked : CheckboxUnchecked, alt: "", "aria-hidden": "true" }) }), _jsx("span", { className: classNames?.radioLabel, children: getLocalizedValue(option.title, locale) })] }, option.value));
            }) })] }, field.fieldName));
/**
 * Renders terms and conditions checkbox with links
 */
const renderTermsCheckbox = (field, checked, onChange, classNames) => {
    const handleContainerClick = (e) => {
        const target = e.target;
        if (target.tagName !== 'A' && !target.closest('a')) {
            onChange(!checked);
        }
    };
    return (_jsxs("div", { className: classNames?.termsWrapper, onClick: handleContainerClick, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(!checked);
            }
        }, role: "checkbox", tabIndex: 0, "aria-checked": checked, children: [_jsx("button", { type: "button", className: classNames?.checkbox, onClick: (e) => {
                    e.stopPropagation();
                    onChange(!checked);
                }, "aria-label": "Terms and conditions checkbox", children: _jsx("img", { src: checked ? CheckboxChecked : CheckboxUnchecked, alt: "", "aria-hidden": "true" }) }), _jsxs("span", { className: classNames?.termsText, children: ["I have read and agreed to the", ' ', _jsx("a", { href: TERMS_LINKS.termsAndConditions.href, className: classNames?.termsLink, onClick: (e) => e.stopPropagation(), children: TERMS_LINKS.termsAndConditions.text }), ",", ' ', _jsx("a", { href: TERMS_LINKS.officialRules.href, className: classNames?.termsLink, onClick: (e) => e.stopPropagation(), children: TERMS_LINKS.officialRules.text }), ",", ' ', _jsx("a", { href: TERMS_LINKS.termsOfService.href, className: classNames?.termsLink, onClick: (e) => e.stopPropagation(), children: TERMS_LINKS.termsOfService.text }), ", and", ' ', _jsx("a", { href: TERMS_LINKS.privacyStatement.href, className: classNames?.termsLink, onClick: (e) => e.stopPropagation(), children: TERMS_LINKS.privacyStatement.text }), "."] })] }, field.fieldName));
};
/**
 * Renders a visual divider
 */
const renderDivider = (field, classNames) => (_jsx("div", { className: classNames?.divider }, field.fieldName));
/**
 * JsonForm - A generic form component driven by JSON schema
 *
 * Renders form fields based on a JSON schema definition.
 * Supports: text, email, number, dropdown, dropdown-multi, checkbox, hidden, termsCheckbox, divider
 *
 * @example
 * ```tsx
 * const schema = {
 *   fields: [
 *     { fieldName: 'schoolId', type: 'hidden' },
 *     { title: { 'en-US': 'Name' }, fieldName: 'name', type: 'text' },
 *     { title: { 'en-US': 'Email' }, fieldName: 'email', type: 'email' },
 *     { title: { 'en-US': 'Countries' }, fieldName: 'countries', type: 'dropdown-multi', options: [...] },
 *     { fieldName: 'divider1', type: 'divider' },
 *     { fieldName: 'acceptedTerms', type: 'termsCheckbox', validation: { required: true } }
 *   ]
 * };
 *
 * const [values, setValues] = useState(() => initializeFormValues(schema));
 *
 * <JsonForm
 *   schema={schema}
 *   values={values}
 *   onChange={(field, value) => setValues(prev => ({ ...prev, [field]: value }))}
 * />
 * ```
 */
const JsonForm = ({ schema, values, onChange, onValidityChange, locale = 'en-US', classNames = {}, readOnlyFields = [], disabledFields = [], children, }) => {
    const [termsAccepted, setTermsAccepted] = useState(false);
    // Calculate validity whenever values or terms change
    useEffect(() => {
        const fieldsValid = validateFormValues(values, schema);
        const isValid = fieldsValid && termsAccepted;
        onValidityChange?.(isValid);
    }, [values, schema, termsAccepted, onValidityChange]);
    /**
     * Handles checkbox toggle for multi-select checkbox groups
     */
    const handleCheckboxToggle = (fieldName, optionValue) => {
        const currentValues = values[fieldName] || [];
        const newValues = currentValues.includes(optionValue)
            ? currentValues.filter((v) => v !== optionValue)
            : [...currentValues, optionValue];
        onChange(fieldName, newValues);
    };
    /**
     * Renders a single field based on its type
     */
    const renderField = (field) => {
        const isReadOnly = readOnlyFields.includes(field.fieldName) || field.readOnly;
        const isDisabled = disabledFields.includes(field.fieldName) || field.disabled;
        switch (field.type) {
            case 'text':
            case 'email':
            case 'number':
                return renderTextInput(field, values[field.fieldName] || '', (value) => onChange(field.fieldName, value), locale, classNames, isReadOnly || false, isDisabled || false);
            case 'dropdown': {
                const dropdownValue = values[field.fieldName] || '';
                const children = getDropdownChildren(field, dropdownValue);
                return (_jsxs(React.Fragment, { children: [renderDropdown(field, dropdownValue, (value) => onChange(field.fieldName, value), locale, classNames, isDisabled || false), children.map(childField => renderField(childField))] }, field.fieldName));
            }
            case 'dropdown-multi':
                return renderMultiSelectDropdown(field, values[field.fieldName] || [], (selectedValues) => onChange(field.fieldName, selectedValues), locale, classNames, isDisabled || false);
            case 'checkbox':
                return renderCheckboxGroup(field, values[field.fieldName] || [], (optionValue) => handleCheckboxToggle(field.fieldName, optionValue), locale, classNames);
            case 'termsCheckbox':
                return renderTermsCheckbox(field, values[field.fieldName] === true, (checked) => onChange(field.fieldName, checked), classNames);
            case 'divider':
                return renderDivider(field, classNames);
            case 'hidden':
                // Hidden fields don't render anything
                return null;
            default:
                return null;
        }
    };
    return (_jsxs("form", { className: classNames.form, children: [schema.fields.map(renderField), children, _jsx(TermsAndConditions, { checked: termsAccepted, onChange: setTermsAccepted })] }));
};
export default JsonForm;
